/**
 * Phase 4.3 — Dual capacity model helper.
 *
 * Doctrine:
 *   - Bucket 3 §3.2 Physical Location vs Production State
 *   - Bucket 3 §3.4 Production states (active_in_bay, held_in_bay,
 *                   held_in_parking, scheduled_but_parked, scheduled,
 *                   parking_lot_inactive, completed_awaiting_pickup,
 *                   picked_up, deleted, archived, estimate_prospect)
 *   - Bucket 5 §5.3 Technician availability states
 *   - Bucket 5 §5.7 Shift and capacity enforcement
 *   - Bucket 2 §2.6 Bay-hold / Pause AutoBump must not inflate Tech OptiTime
 *
 * Why this helper exists
 * ----------------------
 * "Capacity" is two distinct doctrinal concepts that the codebase has
 * historically modeled with overlapping vocabulary:
 *
 *   • TECHNICIAN WORK CAPACITY — how much work can this technician
 *     personally execute right now? Driven by `max_concurrent_jobs`,
 *     current active load, skill fit, presence, break/absence state.
 *     This is the question smart-assign's `concurrencyLoadMap` (at
 *     `supabase/functions/smart-assign/index.ts:539-558` and the
 *     concurrency-filter at lines 2502-2519) actually answers.
 *
 *   • SCHEDULE / BAY CAPACITY — does the shop's physical bay/lane
 *     schedule have room? Driven by bay occupancy (`bay_id` non-null
 *     for an `active_in_bay` / `held_in_bay` ticket), bay-type
 *     compatibility, specialty bays, parking-to-bay confirmation, and
 *     OptiFinish chains. This is what cascade and bay-overlap rules
 *     answer.
 *
 * A technician CAN have work capacity while no bay is available
 * (the lane is full). A bay CAN be available while the technician
 * has no work capacity (over `max_concurrent_jobs`, on break, etc.).
 * These are independent verdicts that must NOT be collapsed.
 *
 * What this module provides
 * -------------------------
 * Pure helpers that compose existing canonical predicates:
 *
 *   • Per-ticket capacity consumption:
 *       - `consumesCurrentBayCapacity(ticket)`
 *       - `consumesFutureScheduleCapacity(ticket)`
 *       - `consumesTechnicianWorkCapacity(ticket)`
 *       - `getCapacityConsumptionForState(state)`
 *
 *   • Per-tech work capacity verdict:
 *       - `TechnicianWorkCapacityState` union (5 states)
 *       - `getTechnicianWorkCapacityState(tech, context)`
 *       - `isTechnicianWorkCapacityBlocked(state)`
 *
 *   • Per-tech-or-bay schedule/bay capacity verdict:
 *       - `ScheduleBayCapacityState` union (5 states)
 *       - `getScheduleBayCapacityState(context)`
 *       - `isScheduleBayCapacityBlocked(state)`
 *
 *   • Combined dual verdict:
 *       - `DualCapacityVerdict` struct
 *       - `getDualCapacityVerdict(techState, bayState)`
 *       - `getCapacityBlockers(verdict)`
 *
 * Phase 4.3 scope: HELPER + TESTS ONLY.
 *   - Additive. Does NOT modify smart-assign, OptiAssign, cascade,
 *     AutoBump, timer, scheduling writes, Tech OptiTime, bay
 *     occupancy math, parking-to-bay or bay-to-parking, or DB schema.
 *   - Reuses Phase 1.1 / 1.3 / 1.2 predicates verbatim.
 *
 * Tech OptiTime invariant (Bucket 2 §2.6):
 * ----------------------------------------
 * This module never reads `opti_time` / `predicted_minutes` /
 * `actual_minutes`. The "tech work capacity" verdict is about COUNT
 * of concurrent active jobs against `max_concurrent_jobs`, not about
 * any time-budget value.
 */

import {
  getProductionState,
  isBayBlockingProductionState,
  isProductionRelevantState,
  isActiveScheduledProductionState,
  type ProductionState,
  type ProductionStateTicketShape,
} from './ticketProductionState';
import type { TechnicianAvailabilityState } from './technicianAvailabilityState';

// =============================================================================
// Per-ticket capacity consumption
// =============================================================================

/**
 * What kind of capacity (if any) does this production state consume?
 *
 *   - `consumesCurrentBay`           — vehicle is physically in a bay AND the
 *     ticket is in `active_in_bay` or `held_in_bay`. Bucket 3 §3.4.4 / §3.4.5.
 *   - `consumesFutureSchedule`       — ticket participates in active scheduling
 *     (Dayboard / OptiBoard active set, cascade, rollover). Bucket 3 §3.4.
 *     Includes `held_in_parking` (still production-relevant, Due-Out tracked)
 *     and `scheduled_but_parked` (Bucket 3 §3.4.3).
 *   - `consumesTechnicianWorkCapacity` — ticket is actively assigned to a
 *     technician AND counts toward that technician's concurrent-job load.
 *     Bucket 5 §5.7. Matches what smart-assign's concurrency filter counts:
 *     `active_in_bay` and `held_in_bay` (the started/in-progress states).
 *     `scheduled` and `scheduled_but_parked` are placed on the schedule but
 *     have not started consuming the tech's active attention budget yet,
 *     so they do NOT consume tech work capacity in this model.
 *     `held_in_parking` is paused while parked — the tech can take another
 *     job, so it also does NOT consume tech work capacity.
 *
 * Terminal / inactive states (`completed_awaiting_pickup`, `picked_up`,
 * `deleted`, `archived`, `parking_lot_inactive`, `estimate_prospect`)
 * consume NONE of the three.
 */
export interface CapacityConsumption {
  consumesCurrentBay: boolean;
  consumesFutureSchedule: boolean;
  consumesTechnicianWorkCapacity: boolean;
}

/**
 * State-level capacity-consumption classifier. Pure: depends only on the
 * production-state value. Sister to `isBayBlockingProductionState` /
 * `isProductionRelevantState` — composes them under one canonical answer.
 */
export function getCapacityConsumptionForState(
  state: ProductionState,
): CapacityConsumption {
  const consumesCurrentBay = isBayBlockingProductionState(state);
  const consumesFutureSchedule = isProductionRelevantState(state);
  // Tech work capacity = actively in-progress on a technician's docket.
  // Matches the smart-assign concurrency-load semantics (started/paused).
  const consumesTechnicianWorkCapacity =
    state === 'active_in_bay' || state === 'held_in_bay';
  return {
    consumesCurrentBay,
    consumesFutureSchedule,
    consumesTechnicianWorkCapacity,
  };
}

/** Does this ticket currently occupy a bay (Bucket 3 §3.4.4 / §3.4.5)? */
export function consumesCurrentBayCapacity(
  ticket: ProductionStateTicketShape,
): boolean {
  return isBayBlockingProductionState(getProductionState(ticket));
}

/**
 * Does this ticket consume future schedule capacity? True for every
 * production-relevant state — including `scheduled_but_parked`
 * (Bucket 3 §3.4.3) and `held_in_parking` (Bucket 3 §3.4.6).
 */
export function consumesFutureScheduleCapacity(
  ticket: ProductionStateTicketShape,
): boolean {
  return isProductionRelevantState(getProductionState(ticket));
}

/**
 * Does this ticket count against the assigned technician's
 * `max_concurrent_jobs` budget right now? Matches smart-assign's
 * `concurrencyLoadMap` semantics: started/paused states only.
 */
export function consumesTechnicianWorkCapacity(
  ticket: ProductionStateTicketShape,
): boolean {
  const s = getProductionState(ticket);
  return s === 'active_in_bay' || s === 'held_in_bay';
}

// =============================================================================
// Technician Work Capacity
// =============================================================================

/**
 * Discrete technician work-capacity verdict.
 *
 *   - 'available'                       — tech is present and has room for more work.
 *   - 'capacity_limited'                — tech is present but at/over `max_concurrent_jobs`.
 *   - 'unavailable_absent'              — manual absent OR off_not_scheduled OR
 *                                          off_shift OR no_show_risk OR awaiting_clock_in.
 *   - 'unavailable_break'               — on_break OR at_lunch.
 *   - 'unavailable_for_specific_job_bay' — present and within capacity but the
 *                                          specific job/bay is incompatible.
 *
 * Maps from a richer `TechnicianAvailabilityState` (Phase 1.2) to a
 * coarser capacity-side answer. The Phase 1.2 helper supplies the
 * caller-evaluated availability state; this helper translates it into
 * the capacity vocabulary.
 */
export type TechnicianWorkCapacityState =
  | 'available'
  | 'capacity_limited'
  | 'unavailable_absent'
  | 'unavailable_break'
  | 'unavailable_for_specific_job_bay';

/** Frozen list of all states. */
export const TECHNICIAN_WORK_CAPACITY_STATES = Object.freeze([
  'available',
  'capacity_limited',
  'unavailable_absent',
  'unavailable_break',
  'unavailable_for_specific_job_bay',
] as const) satisfies ReadonlyArray<TechnicianWorkCapacityState>;

/**
 * Caller-supplied context for the technician work-capacity verdict.
 *
 * The helper does NOT load tickets, query the DB, or recompute
 * `max_concurrent_jobs` math. The caller (smart-assign, OptiAssign,
 * board UI) already has the availability state from Phase 1.2 and
 * the concurrency load from its own pipeline; this helper just
 * translates those signals into the capacity-side answer.
 */
export interface TechnicianWorkCapacityContext {
  /**
   * The tech's canonical availability state (from
   * `getTechnicianAvailabilityState`). This drives `unavailable_*`
   * verdicts so they line up with Bucket 5 doctrine.
   */
  availabilityState: TechnicianAvailabilityState;
  /**
   * Optional: current concurrent-job load against `max_concurrent_jobs`.
   * When both are provided AND `currentLoad >= maxConcurrentJobs`, the
   * helper returns `capacity_limited` even if `availabilityState`
   * resolved to `present` (smart-assign's filter at index.ts:2512
   * uses the same semantics).
   */
  currentLoad?: number;
  /** Optional: the tech's `max_concurrent_jobs` setting. */
  maxConcurrentJobs?: number;
}

/**
 * Compute the technician work-capacity verdict from an availability
 * state + optional load info.
 *
 * Resolution order (terminal first):
 *   1. `absent` / `off_*` / `no_show_risk` / `awaiting_clock_in`
 *      → `unavailable_absent`.
 *   2. `on_break` / `at_lunch` → `unavailable_break`.
 *   3. `unavailable_for_specific_job_bay` → same name.
 *   4. `capacity_limited` (from availability) → `capacity_limited`.
 *   5. If `currentLoad >= maxConcurrentJobs` (when both finite) →
 *      `capacity_limited`.
 *   6. `present` / `after_hours_active` → `available`.
 */
export function getTechnicianWorkCapacityState(
  context: TechnicianWorkCapacityContext,
): TechnicianWorkCapacityState {
  switch (context.availabilityState) {
    case 'absent':
    case 'off_not_scheduled':
    case 'off_shift':
    case 'no_show_risk':
    case 'awaiting_clock_in':
    case 'scheduled':
      return 'unavailable_absent';
    case 'on_break':
    case 'at_lunch':
      return 'unavailable_break';
    case 'unavailable_for_specific_job_bay':
      return 'unavailable_for_specific_job_bay';
    case 'capacity_limited':
      return 'capacity_limited';
    case 'present':
    case 'after_hours_active':
      // Fall through to load check below.
      break;
  }
  // Numeric over-capacity check (smart-assign parity).
  if (
    Number.isFinite(context.currentLoad as number) &&
    Number.isFinite(context.maxConcurrentJobs as number) &&
    (context.currentLoad as number) >= (context.maxConcurrentJobs as number)
  ) {
    return 'capacity_limited';
  }
  return 'available';
}

/** Is this work-capacity verdict a blocker for assigning new work? */
export function isTechnicianWorkCapacityBlocked(
  state: TechnicianWorkCapacityState,
): boolean {
  return state !== 'available';
}

// =============================================================================
// Schedule / Bay Capacity
// =============================================================================

/**
 * Discrete schedule/bay capacity verdict.
 *
 *   - 'bay_available'                       — at least one compatible bay
 *                                              slot is open right now.
 *   - 'bay_occupied'                        — every compatible bay slot is
 *                                              currently held by an
 *                                              `active_in_bay` / `held_in_bay`
 *                                              ticket.
 *   - 'bay_type_mismatch'                   — ticket requires a bay type
 *                                              that no available bay satisfies
 *                                              (specialty, lift, alignment).
 *   - 'parking_to_bay_confirmation_required' — vehicle is parked; a physical
 *                                              move + bay confirmation is
 *                                              required before any in-bay
 *                                              scheduling decision (Phase 2.4).
 *   - 'bay_capacity_unknown'                — caller has no signal.
 */
export type ScheduleBayCapacityState =
  | 'bay_available'
  | 'bay_occupied'
  | 'bay_type_mismatch'
  | 'parking_to_bay_confirmation_required'
  | 'bay_capacity_unknown';

/** Frozen list of all states. */
export const SCHEDULE_BAY_CAPACITY_STATES = Object.freeze([
  'bay_available',
  'bay_occupied',
  'bay_type_mismatch',
  'parking_to_bay_confirmation_required',
  'bay_capacity_unknown',
] as const) satisfies ReadonlyArray<ScheduleBayCapacityState>;

/**
 * Caller-supplied context for the schedule/bay capacity verdict.
 *
 * Bay capacity depends on facility geometry, current bay assignments,
 * specialty-bay rules, and (for parked vehicles) the Phase 2.4
 * parking-to-bay confirmation. This helper does NOT recompute bay
 * occupancy — it composes signals the caller already has.
 */
export interface ScheduleBayCapacityContext {
  /**
   * True iff at least one compatible bay slot is open for the candidate
   * ticket. Caller computes this from bay occupancy + bay-type matching
   * + tech bay assignments.
   */
  hasCompatibleBaySlot?: boolean;
  /**
   * True iff every otherwise-compatible bay slot is currently occupied
   * by a bay-blocking ticket. Used to distinguish `bay_occupied`
   * (capacity will free up) from `bay_type_mismatch` (capacity will
   * never free up for THIS ticket).
   */
  everyCompatibleSlotOccupied?: boolean;
  /**
   * True iff the ticket requires a specialty / lift / alignment bay
   * that NO bay in the facility satisfies (independent of occupancy).
   */
  requiresIncompatibleBayType?: boolean;
  /**
   * True iff the ticket's vehicle is currently parked (Bucket 3
   * §3.4.3 / §3.4.6 — `scheduled_but_parked` or `held_in_parking`).
   * When true, any in-bay scheduling decision requires the Phase 2.4
   * parking-to-bay confirmation BEFORE the bay capacity question can
   * be answered as `bay_available`.
   */
  requiresParkingToBayConfirmation?: boolean;
}

/**
 * Compute the schedule/bay capacity verdict.
 *
 * Resolution order:
 *   1. `requiresIncompatibleBayType` → `bay_type_mismatch`.
 *   2. `requiresParkingToBayConfirmation` →
 *      `parking_to_bay_confirmation_required`.
 *   3. `hasCompatibleBaySlot === true` → `bay_available`.
 *   4. `hasCompatibleBaySlot === false` AND
 *      `everyCompatibleSlotOccupied === true` → `bay_occupied`.
 *   5. otherwise → `bay_capacity_unknown`.
 */
export function getScheduleBayCapacityState(
  context: ScheduleBayCapacityContext,
): ScheduleBayCapacityState {
  if (context.requiresIncompatibleBayType === true) return 'bay_type_mismatch';
  if (context.requiresParkingToBayConfirmation === true) {
    return 'parking_to_bay_confirmation_required';
  }
  if (context.hasCompatibleBaySlot === true) return 'bay_available';
  if (
    context.hasCompatibleBaySlot === false &&
    context.everyCompatibleSlotOccupied === true
  ) {
    return 'bay_occupied';
  }
  return 'bay_capacity_unknown';
}

/** Is this bay-capacity verdict a blocker for placing new work? */
export function isScheduleBayCapacityBlocked(
  state: ScheduleBayCapacityState,
): boolean {
  return state !== 'bay_available';
}

// =============================================================================
// Combined dual verdict
// =============================================================================

/**
 * Combined dual-capacity verdict. The two sides are deliberately kept
 * orthogonal so consumers can distinguish "tech blocked, bay free"
 * from "tech free, bay blocked" — the core doctrinal claim of this
 * module.
 */
export interface DualCapacityVerdict {
  technicianWorkCapacity: TechnicianWorkCapacityState;
  scheduleBayCapacity: ScheduleBayCapacityState;
  /** Derived: is the technician side blocking new work? */
  technicianBlocked: boolean;
  /** Derived: is the schedule/bay side blocking new work? */
  bayBlocked: boolean;
  /**
   * Derived: true iff EXACTLY ONE side is blocked. The signature
   * "I have tech but no bay" or "I have a bay but no tech" case
   * Bucket 5 §5.7 calls out as a distinct planning decision.
   */
  exactlyOneBlocked: boolean;
  /** Derived: true iff both sides are blocked. */
  bothBlocked: boolean;
  /** Derived: true iff neither side is blocked. */
  bothAvailable: boolean;
}

/**
 * Build the combined dual verdict from the two per-side states.
 * Stateless composition.
 */
export function getDualCapacityVerdict(
  technicianWorkCapacity: TechnicianWorkCapacityState,
  scheduleBayCapacity: ScheduleBayCapacityState,
): DualCapacityVerdict {
  const technicianBlocked = isTechnicianWorkCapacityBlocked(technicianWorkCapacity);
  const bayBlocked = isScheduleBayCapacityBlocked(scheduleBayCapacity);
  return {
    technicianWorkCapacity,
    scheduleBayCapacity,
    technicianBlocked,
    bayBlocked,
    exactlyOneBlocked: technicianBlocked !== bayBlocked,
    bothBlocked: technicianBlocked && bayBlocked,
    bothAvailable: !technicianBlocked && !bayBlocked,
  };
}

/**
 * Enumerate the specific blockers in a verdict.
 *
 * Returns at most two non-`'none'`-style codes — one per side. Useful
 * for UI badges and audit rows where the caller wants to render WHY
 * each side is blocked without re-deriving from the verdict struct.
 */
export type CapacityBlocker =
  | TechnicianWorkCapacityState
  | ScheduleBayCapacityState;

export function getCapacityBlockers(verdict: DualCapacityVerdict): CapacityBlocker[] {
  const blockers: CapacityBlocker[] = [];
  if (verdict.technicianBlocked) blockers.push(verdict.technicianWorkCapacity);
  if (verdict.bayBlocked) blockers.push(verdict.scheduleBayCapacity);
  return blockers;
}

/**
 * Doctrine sentinel — does the helper module separate the two
 * concepts as required by Bucket 3 §3.2 and Bucket 5 §5.7?
 *
 * Pure assertion: the union types are disjoint AND no value appears
 * on both sides. Used by the source-scan drift guard in
 * `dualCapacityModel.test.ts` to pin the doctrinal split.
 */
export function separatesTechnicianWorkCapacityFromBayCapacity(): true {
  const techNames = new Set<string>(TECHNICIAN_WORK_CAPACITY_STATES);
  const bayNames = new Set<string>(SCHEDULE_BAY_CAPACITY_STATES);
  for (const t of techNames) {
    if (bayNames.has(t)) {
      throw new Error(
        `dualCapacityModel doctrine violation: '${t}' appears in both ` +
          `TECHNICIAN_WORK_CAPACITY_STATES and SCHEDULE_BAY_CAPACITY_STATES`,
      );
    }
  }
  return true;
}
